import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "@tanstack/react-router";
import { useAuthStore } from "@shared/store/auth";
import { useState } from "react";
import { ArrowRight, Loader2, LockKeyhole, Mail, Shield } from "lucide-react";

const schema = z.object({
  email: z.string().email("Email no válido"),
  password: z.string().min(1, "Introduce la contraseña"),
  remember: z.boolean().default(false),
});

type FormData = z.infer<typeof schema>;

export function LoginPage() {
  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "", remember: false },
  });

  const onSubmit = async (data: FormData) => {
    setServerError(null);
    try {
      await login(data.email, data.password, data.remember);

      const role = useAuthStore.getState().me?.roles?.[0] ?? "empleado";
      const panelRoles = ["super-admin", "admin", "gerente"];

      navigate({
        to: panelRoles.includes(role) ? "/" : "/rrhh",
      });
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Credenciales incorrectas.";

      setServerError(msg);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="auth-login-card">
      <div className="auth-login-header">
        <div className="auth-login-icon">
          <Shield size={28} strokeWidth={2.2} />
        </div>

        <div className="auth-login-kicker">Acceso corporativo</div>

        <h2 className="auth-login-title">Iniciar sesión</h2>

        <p className="auth-login-subtitle">
          Entra al workspace privado de INPRONEXUS.
        </p>
      </div>

      <div className="auth-fields">
        <label className="auth-field">
          <span className="auth-label">Email corporativo</span>

          <div className="auth-input-wrap">
            <Mail size={17} className="auth-input-icon" />

            <input
              type="email"
              autoComplete="email"
              className="auth-input"
              placeholder="admin@empresa.com"
              {...register("email")}
            />
          </div>

          {errors.email && (
            <p className="auth-error-text">{errors.email.message}</p>
          )}
        </label>

        <label className="auth-field">
          <span className="auth-label">Contraseña</span>

          <div className="auth-input-wrap">
            <LockKeyhole size={17} className="auth-input-icon" />

            <input
              type="password"
              autoComplete="current-password"
              className="auth-input"
              placeholder="Introduce tu contraseña"
              {...register("password")}
            />
          </div>

          {errors.password && (
            <p className="auth-error-text">{errors.password.message}</p>
          )}
        </label>
      </div>

      <div className="auth-options">
        <label className="auth-check">
          <input
            type="checkbox"
            className="auth-checkbox"
            {...register("remember")}
          />
          <span>Recordarme</span>
        </label>
      </div>

      {serverError && <div className="auth-error-box">{serverError}</div>}

      <button type="submit" disabled={isSubmitting} className="auth-submit">
        {isSubmitting ? (
          <>
            <Loader2 size={17} className="animate-spin" />
            Entrando...
          </>
        ) : (
          <>
            Entrar al panel
            <ArrowRight size={17} />
          </>
        )}
      </button>
    </form>
  );
}